import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { admin as adminApi, ApiError, type PlanLimits } from "../lib/api";
import { useSession } from "../lib/session";
import { QueryError } from "../components/QueryError";
import { PlanTiers } from "../components/PlanTiers";

// Admin: plan + medidor de uso vs límite (plan_limits) + crear sucursal con
// enforcement (branch_create). Si el plan topa, el RPC lo bloquea y se muestra.
export const Route = createFileRoute("/admin")({ component: Admin });

function Admin() {
  const { role } = useSession();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [bn, setBn] = useState(""); const [bc, setBc] = useState("");

  // Tier: fuente legal es organizations.plan_tier; subscriptions opcional (status).
  const sub = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      const [{ data: org }, { data: subs }] = await Promise.all([
        supabase.from("organizations").select("plan_tier").maybeSingle(),
        supabase.from("subscriptions").select("status, current_period_end").maybeSingle(),
      ]);
      const o = org as { plan_tier?: string } | null;
      const s = subs as { status?: string; current_period_end?: string | null } | null;
      return {
        plan_tier: o?.plan_tier ?? "free",
        status: s?.status ?? "active",
        current_period_end: s?.current_period_end ?? null,
      };
    },
  });

  const tier = sub.data?.plan_tier ?? "free";
  const limits = useQuery({ queryKey: ["plan_limits", tier], queryFn: () => adminApi.planLimits(tier) });

  // Conteos actuales del tenant (RLS filtra).
  const usage = useQuery({
    queryKey: ["usage"],
    queryFn: async () => {
      const [b, u, t, p] = await Promise.all([
        supabase.from("branches").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("users").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("pos_terminals").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("products").select("id", { count: "exact", head: true }).eq("is_active", true),
      ]);
      return { branches: b.count ?? 0, users: u.count ?? 0, terminals: t.count ?? 0, skus: p.count ?? 0 };
    },
  });

  const createBranch = useMutation({
    mutationFn: () => adminApi.branchCreate(bn, bc),
    onSuccess: () => { setBn(""); setBc(""); setMsg("Sucursal creada."); void qc.invalidateQueries({ queryKey: ["usage"] }); },
    onError: (e) => setMsg(e instanceof ApiError ? (e.isPlanLimit ? `🔒 ${e.message}` : e.message) : String(e)),
  });

  const lim = limits.data as PlanLimits | undefined;
  const u = usage.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Configuración</h1>
      <QueryError error={sub.error ?? limits.error ?? usage.error} />

      <section className="rounded-xl border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">Plan actual: <span className="uppercase text-brand">{tier}</span></h2>
          <span className="text-xs text-grafito-400">{sub.data?.status}</span>
        </div>
        {lim && u && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Meter label="Sucursales" used={u.branches} max={lim.branches} />
            <Meter label="Usuarios" used={u.users} max={lim.users} />
            <Meter label="Cajas" used={u.terminals} max={lim.terminals} />
            <Meter label="Productos" used={u.skus} max={lim.skus} />
          </div>
        )}
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-xl font-bold">Planes</h2>
          <p className="text-sm text-grafito-400">
            Sube de plan cuando tu ferretería crezca. Los límites los aplica el servidor — funciona igual en la app web y la app nativa.
          </p>
        </div>
        <PlanTiers current={tier} onPick={(id) => setMsg(`Cambio a "${id}" — la pasarela de pago (Paddle) se conecta cuando esté lista. Por ahora el plan se cambia desde la BD.`)} />
      </section>

      {(role === "owner" || role === "admin") && (
        <section className="rounded-xl border bg-white p-5">
          <h2 className="mb-3 font-semibold">Nueva sucursal</h2>
          <div className="flex flex-wrap items-end gap-3">
            <input value={bn} onChange={(e) => setBn(e.target.value)} placeholder="Nombre" className="rounded-lg border px-3 py-2 outline-none focus:border-brand" />
            <input value={bc} onChange={(e) => setBc(e.target.value)} placeholder="Código (ej 002)" className="w-32 rounded-lg border px-3 py-2 outline-none focus:border-brand" />
            <button onClick={() => createBranch.mutate()} disabled={!bn || !bc || createBranch.isPending} className="btn btn-primary disabled:opacity-40">Crear</button>
          </div>
          {msg && <p className="mt-3 text-sm text-grafito-700">{msg}</p>}
          <p className="mt-2 text-xs text-grafito-400">El plan limita las sucursales. Si topas, sube de plan — el control es del servidor (web y app nativa por igual).</p>
        </section>
      )}
    </div>
  );
}

function Meter({ label, used, max }: { label: string; used: number; max: number | null }) {
  const pct = max == null ? 0 : Math.min(100, (used / max) * 100);
  const full = max != null && used >= max;
  return (
    <div className="rounded-lg border p-3">
      <div className="flex justify-between text-xs"><span className="text-grafito-400">{label}</span><span>{used}/{max ?? "∞"}</span></div>
      <div className="mt-2 h-2 rounded bg-grafito-100">
        {max != null && <div className={`h-2 rounded ${full ? "bg-red-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />}
      </div>
    </div>
  );
}
